import { useCallback, useEffect } from 'react';
import { BUTTON_TO_OPEN_CHAT_ID } from '../../../../../../components/AppWrap/components/OpenChatButton/OpenChatButton';
import useWidth from '../../../../../../components/Hooks/useWidth';
import { findMainChatBetweenStoreAndBrand } from '../../../../../../services/modules/chats/chats.repository';
import { sleep } from '../../../../../../utils/generics';
import { ChatType } from '../../../../types/chat.types';
import { useChatModalContext } from '../../../chatModal/ChatModal.context';
import {
  closeActiveChat,
  minimizeActiveChat,
  openChatCreation,
  openGroupChatCreation,
  openGroupedRooms,
  openPrivateChat,
  openRoomsOfGroup,
  removeChatFromMinimized,
  updateSelectedChat,
} from '../../reducer/chatReducer.actions';
import { ChatViewType } from '../../reducer/chatReducer.types';
import { ChatStateWithOptionalId } from '../useChats/reducer/chatListReducer.types';
import { UseChatViewStateHandlerParams } from './useChatViewStateHandler.types';

const useChatViewStateHandler = ({
  dispatch,
  chatsListState,
  state,
}: UseChatViewStateHandlerParams) => {
  const { mobileWidth } = useWidth();

  const { close: closeChatConfigModal, open, isOpen } = useChatModalContext();

  const goToChatListOfGroup = useCallback(
    (groupMetaId: string) => {
      dispatch(openRoomsOfGroup({ groupMetaId }));
    },
    [chatsListState.state.chatRooms],
  );

  const goToGroupedRooms = useCallback(() => {
    dispatch(openGroupedRooms());
  }, []);

  const changeSelectedChat = async (chatId?: string, minimizeCurrentActiveChat?: boolean) => {
    const selectedChat = chatId ? await chatsListState.getChatById(chatId) : undefined;

    if (selectedChat) {
      setTimeout(() => {
        goToGroupedRooms();
      }, 350);

      if (mobileWidth) {
        dispatch(updateSelectedChat({ chat: selectedChat, minimizeCurrentActiveChat }));

        setTimeout(() => {
          closeChatConfigModal();
        }, 250);
        return;
      }

      closeChatConfigModal();
    }

    dispatch(updateSelectedChat({ chat: selectedChat, minimizeCurrentActiveChat }));
  };

  const asyncChangeSelectedChat = async (chatId: string) => {
    changeSelectedChat(chatId);

    await sleep(100);
  };

  const handleCloseActiveChat = useCallback(() => {
    dispatch(closeActiveChat());
  }, []);

  const handleMinimizeActiveChat = useCallback(() => {
    dispatch(minimizeActiveChat());
  }, []);

  const handleOpenChatCreation = useCallback(() => {
    dispatch(openChatCreation());
  }, []);

  const openPrivateChatBetweenStoreAndBrandOrChatCreation = async (
    storeId: string,
    brandId: string,
  ) => {
    // TODO: PARECE NÃO FAZER SENTIDO A GENTE FAZER UM FIND AQUI PARA DEPOIS MANDAR APENAS O ID PARA O changeSelectedChat
    const response = await findMainChatBetweenStoreAndBrand({
      brandId,
      storeId,
    });

    if (!response.ok || !response.data) {
      const openChatButtonEl = document.querySelector<HTMLButtonElement>(
        `#${BUTTON_TO_OPEN_CHAT_ID}`,
      );
      if (openChatButtonEl) {
        open(openChatButtonEl);
      }
      handleOpenChatCreation();
      return;
    }

    await changeSelectedChat(response.data.id);
  };

  const openChatOfCaseOrCreate = async (caseId: string) => {
    const chat = await chatsListState.getChatByCaseId(caseId);

    if (chat) {
      await changeSelectedChat(chat.id);
      return;
    }

    const createdChat = await chatsListState.createChatIfNotExists({
      caseId,
      type: ChatType.TOPIC,
    });

    if (createdChat) {
      await changeSelectedChat(createdChat.id);
    }
  };

  const handleOpenGroupChatCreation = useCallback(() => {
    dispatch(openGroupChatCreation());
  }, []);

  const handleOpenPrivateChat = useCallback((chat: ChatStateWithOptionalId) => {
    closeChatConfigModal();
    dispatch(openPrivateChat(chat));
    setTimeout(() => {
      goToGroupedRooms();
    }, 150);
  }, []);

  const handleRemoveChatFromMinimized = useCallback((chatId: string) => {
    dispatch(removeChatFromMinimized({ chatId }));
  }, []);

  useEffect(() => {
    if (chatsListState.state.totalItems === 1 && state.view.type === ChatViewType.GroupedRooms) {
      goToChatListOfGroup(chatsListState.state.chatRooms[0].metaId);
    }
  }, [chatsListState.state.chatRooms, state.view.type]);

  useEffect(() => {
    if (state.view.type === ChatViewType.GroupCreation && !isOpen) {
      setTimeout(handleOpenChatCreation, 100);
    }
  }, [isOpen]);

  return {
    goToGroupedRooms,
    changeSelectedChat,
    goToChatListOfGroup,
    openChatOfCaseOrCreate,
    asyncChangeSelectedChat,
    openPrivateChat: handleOpenPrivateChat,
    closeActiveChat: handleCloseActiveChat,
    openChatCreation: handleOpenChatCreation,
    minimizeActiveChat: handleMinimizeActiveChat,
    openPrivateChatBetweenStoreAndBrandOrChatCreation,
    openGroupChatCreation: handleOpenGroupChatCreation,
    removeChatFromMinimized: handleRemoveChatFromMinimized,
  };
};

export default useChatViewStateHandler;
